import React, { useState, useEffect, useContext } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import ProductCard from '../components/ProductCard';
import { AuthContext, CartContext } from '../App';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { ShoppingCart, Minus, Plus, FileText, Lock, ChevronRight, Package } from 'lucide-react';
import SEO, { BreadcrumbSchema } from '../components/SEO';
import { EditModeProvider, AdminToolbar } from '../components/EditableContent';
import CMSBlockRenderer from '../components/CMSBlockRenderer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function ProductDetailPage() {
  const { slug } = useParams();
  const { user } = useContext(AuthContext);
  const { addToCart } = useContext(CartContext);
  
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [category, setCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);

  const [storeSettings, setStoreSettings] = useState({ backorders_enabled: false });

  useEffect(() => {
    fetchProduct();
    fetchStoreSettings();
  }, [slug]);

  const fetchStoreSettings = async () => {
    try {
      const res = await axios.get(`${API}/store-settings`);
      setStoreSettings(res.data.settings);
    } catch (err) {
      // Default to backorders disabled
    }
  };

  const fetchProduct = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/products/${slug}`);
      setProduct(res.data.product);
      setRelated(res.data.related || []);
      setCategory(res.data.category);
      setSelectedImage(0);
      setQuantity(res.data.product?.min_order_qty || 1);
    } catch (err) {
      console.error('Error fetching product:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = async () => {
    if (!user) {
      toast.error('Please login to add items to cart');
      return;
    }
    try {
      await addToCart(product.id, quantity);
      toast.success(`Added ${quantity} item(s) to cart`);
    } catch (err) {
      toast.error('Failed to add to cart');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="animate-pulse">
            <div className="h-8 bg-zinc-800 rounded w-1/4 mb-8" />
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div className="aspect-square bg-zinc-800 rounded" />
              <div className="space-y-4">
                <div className="h-6 bg-zinc-800 rounded w-1/3" />
                <div className="h-10 bg-zinc-800 rounded w-2/3" />
                <div className="h-24 bg-zinc-800 rounded" />
              </div>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (!product) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <h1 className="text-2xl font-bold text-foreground">Product not found</h1>
          <Link to="/products" className="text-[#78BE20] hover:underline mt-4 inline-block">
            Back to Products
          </Link>
        </div>
      </Layout>
    );
  }

  const userTier = user?.account_tier || 'retail';
  const price = product.pricing[userTier] || product.pricing.retail;
  const inStock = product.stock_qty > 0;
  // Backorders only allowed if BOTH global setting AND product setting are enabled
  const allowBackorder = storeSettings.backorders_enabled && product.allow_backorder === true;
  const canOrder = inStock || allowBackorder;
  const backorderQty = quantity > product.stock_qty ? quantity - product.stock_qty : 0;

  // Breadcrumb items for SEO
  const breadcrumbItems = [
    { name: 'Home', url: '/' },
    { name: 'Products', url: '/products' },
    ...(category ? [{ name: category.name, url: `/category/${category.slug}` }] : []),
    { name: product.name, url: `/products/${product.slug}` }
  ];

  return (
    <Layout>
      <SEO 
        title={product.name}
        description={product.description?.substring(0, 160)}
        keywords={`${product.name}, ${product.brand}, ${category?.name || ''}, industrial technology, Australia`}
        image={product.images?.[0]}
        url={`/products/${product.slug}`}
        type="product"
        product={product}
      />
      <BreadcrumbSchema items={breadcrumbItems} />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="product-detail-page">
        {/* Breadcrumb */}
        <nav className="flex items-center space-x-2 text-sm text-muted-foreground mb-8">
          <Link to="/" className="hover:text-[#78BE20]">Home</Link>
          <ChevronRight className="h-4 w-4" />
          <Link to="/products" className="hover:text-[#78BE20]">Products</Link>
          {category && (
            <>
              <ChevronRight className="h-4 w-4" />
              <Link to={`/category/${category.slug}`} className="hover:text-[#78BE20]">{category.name}</Link>
            </>
          )}
          <ChevronRight className="h-4 w-4" />
          <span className="text-foreground">{product.name}</span>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Images */}
          <div className="space-y-4">
            <div className="aspect-square bg-zinc-900 rounded-sm overflow-hidden border border-border">
              {product.images?.[selectedImage] ? (
                <img
                  src={product.images[selectedImage]}
                  alt={product.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-zinc-600">
                  <Package className="h-24 w-24" />
                </div>
              )}
            </div>
            {product.images?.length > 1 && (
              <div className="flex gap-2 overflow-x-auto pb-2">
                {product.images.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setSelectedImage(idx)}
                    className={`flex-shrink-0 w-20 h-20 rounded-sm overflow-hidden border-2 transition-colors ${
                      selectedImage === idx ? 'border-[#78BE20]' : 'border-border'
                    }`}
                  >
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Info */}
          <div className="space-y-6">
            <div>
              <p className="text-sm text-zinc-500 uppercase tracking-wide">{product.brand}</p>
              <h1 className="text-3xl font-black text-foreground mt-1" data-testid="product-name">
                {product.name}
              </h1>
              <p className="text-sm text-muted-foreground font-mono mt-2">SKU: {product.sku}</p>
            </div>

            {/* Pricing */}
            <div className="bg-card border border-border rounded-sm p-6">
              <div className="flex items-end justify-between">
                <div>
                  <p className="text-3xl font-black text-[#78BE20] font-mono" data-testid="product-price">
                    ${price.toFixed(2)}
                  </p>
                  {user && userTier !== 'retail' && (
                    <p className="text-sm text-zinc-500">
                      <span className="line-through">${product.pricing.retail.toFixed(2)}</span>
                      <span className="ml-2 text-[#78BE20]">{userTier.toUpperCase()} PRICE</span>
                    </p>
                  )}
                </div>
                {!user && (
                  <div className="flex items-center text-sm text-zinc-500">
                    <Lock className="h-4 w-4 mr-1" />
                    <Link to="/login" className="text-[#78BE20] hover:underline">Login</Link>
                    <span className="mx-1">or</span>
                    <Link to="/trade-application" className="text-[#78BE20] hover:underline">Apply for Trade</Link>
                  </div>
                )}
              </div>

              {/* Stock Status */}
              <div className="mt-4 flex flex-col gap-1">
                <div className="flex items-center">
                  <div className={`w-3 h-3 rounded-full mr-2 ${inStock ? 'bg-[#78BE20]' : allowBackorder ? 'bg-yellow-500' : 'bg-red-500'}`} />
                  <span className={inStock ? 'text-[#78BE20]' : allowBackorder ? 'text-yellow-500' : 'text-red-500'}>
                    {inStock ? `${product.stock_qty} in stock` : allowBackorder ? 'Available on Backorder' : 'Out of Stock'}
                  </span>
                </div>
                {allowBackorder && inStock && (
                  <p className="text-xs text-zinc-400 ml-5">Backorders accepted if ordering more than available stock</p>
                )}
              </div>

              {/* Quantity & Add to Cart */}
              <div className="mt-6">
                <div className="flex items-center gap-4">
                  <div className="flex items-center border border-border rounded-sm">
                    <button
                      onClick={() => setQuantity(Math.max(product.min_order_qty || 1, quantity - 1))}
                      className="p-3 hover:bg-zinc-800 transition-colors"
                      disabled={quantity <= (product.min_order_qty || 1)}
                    >
                      <Minus className="h-4 w-4" />
                    </button>
                    <input
                      type="number"
                      min={product.min_order_qty || 1}
                      max={allowBackorder ? (product.max_order_qty || 9999) : (product.max_order_qty || product.stock_qty)}
                      value={quantity}
                      onChange={(e) => {
                        const val = parseInt(e.target.value) || (product.min_order_qty || 1);
                        const min = product.min_order_qty || 1;
                        const max = allowBackorder ? (product.max_order_qty || 9999) : (product.max_order_qty || product.stock_qty);
                        setQuantity(Math.max(min, Math.min(max, val)));
                      }}
                      className="w-14 text-center font-mono bg-transparent border-none outline-none text-foreground [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      data-testid="product-qty-input"
                    />
                    <button
                      onClick={() => setQuantity(Math.min(allowBackorder ? (product.max_order_qty || 9999) : (product.max_order_qty || product.stock_qty), quantity + 1))}
                      className="p-3 hover:bg-zinc-800 transition-colors"
                      disabled={quantity >= (allowBackorder ? (product.max_order_qty || 9999) : (product.max_order_qty || product.stock_qty))}
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                  </div>
                  <Button
                    onClick={handleAddToCart}
                    disabled={!canOrder}
                  className="flex-1 bg-[#78BE20] text-black hover:bg-[#65a31b] h-12"
                  data-testid="add-to-cart-btn"
                >
                  <ShoppingCart className="h-5 w-5 mr-2" />
                  Add to Cart
                </Button>
                </div>
                {backorderQty > 0 && allowBackorder && (
                  <div className="mt-3 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-md">
                    <p className="text-sm text-yellow-500">
                      <strong>{product.stock_qty > 0 ? product.stock_qty : 0}</strong> will ship immediately, <strong>{backorderQty}</strong> will be backordered
                    </p>
                  </div>
                )}
                {(product.min_order_qty > 1 || product.max_order_qty) && (
                  <p className="text-xs text-zinc-500 mt-2">
                    {product.min_order_qty > 1 && `Min order: ${product.min_order_qty}`}
                    {product.min_order_qty > 1 && product.max_order_qty && ' · '}
                    {product.max_order_qty && `Max order: ${product.max_order_qty}`}
                  </p>
                )}
              </div>
            </div>

            {/* Description */}
            <div>
              <h2 className="text-lg font-bold text-foreground mb-2">Description</h2>
              <p className="text-muted-foreground">{product.description}</p>
            </div>

            {/* Specifications */}
            {product.specs?.length > 0 && (
              <div>
                <h2 className="text-lg font-bold text-foreground mb-4">Specifications</h2>
                <div className="border border-border rounded-sm overflow-hidden">
                  <table className="w-full">
                    <tbody>
                      {product.specs.map((spec, idx) => (
                        <tr key={idx} className={idx % 2 === 0 ? 'bg-card' : 'bg-background'}>
                          <td className="px-4 py-3 text-sm font-medium text-muted-foreground w-1/3">
                            {spec.name}
                          </td>
                          <td className="px-4 py-3 text-sm text-foreground font-mono">
                            {spec.value}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Datasheets */}
            {product.datasheets?.length > 0 && (
              <div>
                <h2 className="text-lg font-bold text-foreground mb-4">Downloads</h2>
                <div className="space-y-2">
                  {product.datasheets.map((url, idx) => (
                    <a
                      key={idx}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center text-[#78BE20] hover:underline"
                    >
                      <FileText className="h-4 w-4 mr-2" />
                      Datasheet {idx + 1}
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Related Products */}
        {related.length > 0 && (
          <div className="mt-16">
            <h2 className="text-2xl font-black text-foreground uppercase tracking-tight mb-8">
              Related Products
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {related.map(product => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          </div>
        )}
      </div>
      <CMSBlockRenderer excludeTypes={[]} />
      <AdminToolbar />
    </Layout>
  );
}

export default function ProductDetailWrapper() {
  return (
    <EditModeProvider pageSlug="products">
      <ProductDetailPage />
    </EditModeProvider>
  );
}
